import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Clock, Building2, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { roles as mockRoles, jobCategories } from '../data/mock';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Roles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, [selectedCategory]);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/roles`, {
        params: { category: selectedCategory || undefined },
      });
      setRoles(response.data);
    } catch (error) {
      // Use mock data as fallback
      let filtered = mockRoles;
      if (selectedCategory) {
        filtered = mockRoles.filter(r => r.category === selectedCategory);
      }
      setRoles(filtered);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(role =>
    role.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? '' : categoryId);
    if (categoryId === selectedCategory) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <Navbar variant="light" />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Browse Roles</h1>
          <p className="text-gray-500">Find opportunities that match your skills and expertise.</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search roles or companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {jobCategories.map(cat => (
                <Badge
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  className="cursor-pointer px-3 py-1.5"
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  {cat.name}
                  {selectedCategory === cat.id && <X className="w-3 h-3 ml-1" />}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            ))
          ) : filteredRoles.length > 0 ? (
            filteredRoles.map(role => (
              <div
                key={role.id}
                onClick={() => navigate(`/roles/${role.id}`)}
                className="bg-white rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100 hover:border-indigo-100"
              >
                <h3 className="font-semibold text-gray-900 mb-1 hover:text-indigo-600 transition-colors">
                  {role.title}
                </h3>
                <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-3">
                  <Building2 className="w-4 h-4" />
                  <span>{role.company}</span>
                </div>
                <p className="text-indigo-600 font-semibold mb-4">{role.payRange || role.pay_range}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {role.type}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {role.location}
                  </span>
                </div>
                <p className="text-gray-500 text-sm line-clamp-2">{role.description}</p>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500">No roles found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Roles;
